import { useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { createIntakeForm, updateIntakeForm } from '../../../services/intakeFormService';

const QUESTION_TYPES = [
  { value: 'text', label: 'Short text' },
  { value: 'textarea', label: 'Long text' },
  { value: 'radio', label: 'Single choice (radio)' },
  { value: 'checkbox', label: 'Multiple choice (checkbox)' },
  { value: 'select', label: 'Dropdown (select)' },
  { value: 'date', label: 'Date' },
];

const questionSchema = z.object({
  id: z.string().min(1),
  type: z.enum(['text', 'textarea', 'checkbox', 'radio', 'select', 'date']),
  label: z.string().min(1, 'Question text is required'),
  required: z.boolean().default(false),
  options: z.array(z.string()).optional(),
});

const formSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  description: z.string().optional().or(z.literal('')),
  schema: z.array(questionSchema).min(1, 'Add at least one question'),
});

function genId() {
  return Math.random().toString(36).slice(2, 10);
}

export default function IntakeFormBuilder({ existingForm, onClose }) {
  const queryClient = useQueryClient();
  const isEditing = Boolean(existingForm);

  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: isEditing
      ? {
          title: existingForm.title,
          description: existingForm.description || '',
          schema: existingForm.schema,
        }
      : {
          title: '',
          description: '',
          schema: [{ id: genId(), type: 'text', label: '', required: false, options: [] }],
        },
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'schema' });
  const watchedSchema = watch('schema');

  const mutation = useMutation({
    mutationFn: (data) =>
      isEditing
        ? updateIntakeForm(existingForm.id, data)
        : createIntakeForm(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['intakeForms'] });
      toast.success(isEditing ? 'Form updated' : 'Form created');
      onClose();
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to save form'),
  });

  const onSubmit = (data) => mutation.mutate(data);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
      <div>
        <label className="block text-sm font-medium text-neutral-700 mb-1">Form title *</label>
        <input type="text" className="input" placeholder="e.g. New Client Intake" {...register('title')} />
        {errors.title && <p className="mt-1 text-sm text-danger-600">{errors.title.message}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-neutral-700 mb-1">Description</label>
        <textarea className="input" rows={2} placeholder="Optional instructions for the client" {...register('description')} />
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-medium text-neutral-700">Questions *</p>
          {errors.schema && !Array.isArray(errors.schema) && (
            <p className="text-sm text-danger-600">{errors.schema.message}</p>
          )}
        </div>

        <div className="space-y-3">
          {fields.map((field, index) => {
            const qType = watchedSchema?.[index]?.type;
            return (
              <div key={field.id} className="border border-neutral-200 rounded-lg p-4 space-y-3">
                <div className="flex gap-3 items-start">
                  <div className="flex-1">
                    <input
                      type="text"
                      className="input"
                      placeholder="Question text *"
                      {...register(`schema.${index}.label`)}
                    />
                    {errors.schema?.[index]?.label && (
                      <p className="mt-1 text-sm text-danger-600">{errors.schema[index].label.message}</p>
                    )}
                  </div>
                  <select className="input w-44 shrink-0" {...register(`schema.${index}.type`)}>
                    {QUESTION_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                  <label className="flex items-center gap-1.5 text-sm text-neutral-600 shrink-0 mt-2.5">
                    <input type="checkbox" className="rounded" {...register(`schema.${index}.required`)} />
                    Required
                  </label>
                  <button
                    type="button"
                    onClick={() => remove(index)}
                    className="text-neutral-400 hover:text-danger-500 mt-2"
                    title="Remove question"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>

                {(qType === 'radio' || qType === 'checkbox' || qType === 'select') && (
                  <OptionsEditor control={control} register={register} questionIndex={index} />
                )}

                <input type="hidden" {...register(`schema.${index}.id`)} />
              </div>
            );
          })}
        </div>

        <button
          type="button"
          onClick={() => append({ id: genId(), type: 'text', label: '', required: false, options: [] })}
          className="mt-3 btn btn-secondary text-sm flex items-center gap-1.5"
        >
          <PlusIcon className="h-4 w-4" />
          Add question
        </button>
      </div>

      <div className="flex justify-end gap-3 pt-2 border-t border-neutral-200">
        <button type="button" onClick={onClose} className="btn btn-secondary">Cancel</button>
        <button type="submit" disabled={mutation.isPending} className="btn btn-primary disabled:opacity-60">
          {mutation.isPending ? 'Saving…' : isEditing ? 'Save changes' : 'Create form'}
        </button>
      </div>
    </form>
  );
}

function OptionsEditor({ control, register, questionIndex }) {
  const { fields, append, remove } = useFieldArray({
    control,
    name: `schema.${questionIndex}.options`,
  });

  return (
    <div className="pl-4 border-l-2 border-neutral-100 space-y-2">
      <p className="text-xs text-neutral-500 font-medium">Options</p>
      {fields.map((opt, optIdx) => (
        <div key={opt.id} className="flex gap-2">
          <input
            type="text"
            className="input text-sm"
            placeholder={`Option ${optIdx + 1}`}
            {...register(`schema.${questionIndex}.options.${optIdx}`)}
          />
          <button type="button" onClick={() => remove(optIdx)} className="text-neutral-400 hover:text-danger-500">
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => append('')}
        className="text-sm text-primary-600 hover:underline"
      >
        + Add option
      </button>
    </div>
  );
}
